import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import { TreeView } from '@mui/lab';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  FormControlLabel,
  Grid,
  IconButton,
  Popover,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  compact,
  Dictionary,
  every,
  flatMap,
  forEach,
  get,
  includes,
  isEmpty,
  keys,
  map,
  orderBy,
  startCase,
  uniq,
  values,
} from 'lodash';
import React, { useState } from 'react';

import {
  modelTypeArea,
  modelTypeBinaryClassifier,
  modelTypeCellSegmentation,
  modelTypesByApiModelValue,
} from 'components/Pages/Jobs/inferenceFieldsOptions';
import {
  ClassificationBinningParams,
  InferenceModelData,
  OrchestrationInference,
  SlideInferenceResults,
} from 'interfaces/calculateFeatures';
import { getModelId, ModelPreferencesById, ModelsByStainByType, ModelUserPreferences } from 'utils/models';
import { OrchestrationBySlideByResultName, OrchestrationBySlideByType } from 'utils/orchestrationSelections';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { BinaryClassifierSelectionMode } from '../../../binaryClassifierSelections';
import { getDetectionLabelOptions } from '../../../PostProcessingActions/utils';
import {
  getDefaultSpotDetectionSetup,
  getProbeDisplayName,
  getSpotDetectionSetupSummary,
  SPOT_DETECTION_RESULT_NAME,
} from '../../../spotDetectionInputSetup';
import ModelRow from './ModelRow';
import { IntensityBins } from './ModelRow/IntensityBins';
import OrchestrationRow from './ModelRow/OrchestrationRow';
import { ProbeCountBinsSetup } from './PerProbeCountBinsOverrides';
import { SpotDetectionInputSetup } from './SpotDetectionInputSetup';

const binaryClassifierMultipleSelectionWarning =
  'Before continuing, make sure the marker set for each selected binary classifier result on a slide does not conflict with the other selected results.';

export interface InferenceModelProps {
  studyId: string;
  stain: string;
  modelType?: string;
  resultName?: string;
  stainModelInferences: ModelsByStainByType[string];
  orchestrations?: OrchestrationInference[];
  slides: Dictionary<SlideInferenceResults>;
  selectedOrchestrations?: OrchestrationBySlideByType;
  setSelectedOrchestrations?: (
    slideIds: string[],
    model: InferenceModelData,
    modelType: string,
    orchestration: OrchestrationInference
  ) => void;
  selectedOrchestrationsByResultName?: OrchestrationBySlideByResultName;
  setSelectedOrchestrationsByResultName?: (
    slideIds: string[],
    resultName: string,
    orchestration: OrchestrationInference
  ) => void;
  setModelPreferencesByModelId?: (modelId: string, userPreferences: ModelUserPreferences) => void;
  modelPreferencesByModelId?: ModelPreferencesById; // TODO: currently holds model + binning preferences - should be separated and renamed
  availableCellSegmentationGeometries?: string[];
  setSelectedOrchestrationsByClassificationType?: (modelType: string, previousModelType?: string) => void;
  setIntensityClassificationBinning?: (modelId: string, binning: ClassificationBinningParams) => void;
  binaryClassifierSelectionMode?: BinaryClassifierSelectionMode;
  setBinaryClassifierSelectionMode?: (mode: BinaryClassifierSelectionMode) => void;
  enableSpotDetectionSetup?: boolean;
  disableIfNoJob?: boolean;
}

const InferenceModel: React.FC<React.PropsWithChildren<InferenceModelProps>> = ({
  studyId,
  stain,
  modelType,
  resultName,
  stainModelInferences,
  orchestrations,
  slides,
  selectedOrchestrations,
  setSelectedOrchestrations,
  selectedOrchestrationsByResultName,
  setSelectedOrchestrationsByResultName,
  modelPreferencesByModelId,
  setModelPreferencesByModelId,
  setSelectedOrchestrationsByClassificationType,
  setIntensityClassificationBinning,
  binaryClassifierSelectionMode,
  setBinaryClassifierSelectionMode,
  availableCellSegmentationGeometries,
  enableSpotDetectionSetup = false,
  disableIfNoJob,
}) => {
  const [anchorElIntensityBins, setAnchorElIntensityBins] = useState<null | HTMLElement>(null);

  const handleCloseIntensityBins = () => {
    setAnchorElIntensityBins(null);
  };

  const modelInferences = modelType ? get(stainModelInferences, modelType) : null;

  const showBinning = includes(['cell_quantification', 'area_quantification'], resultName);
  const showSpotDetectionSetup = Boolean(enableSpotDetectionSetup && resultName && setModelPreferencesByModelId);
  const isCellSegmentationModel = modelType === modelTypeCellSegmentation.apiModelValue;
  const shouldExpandByDefault = showSpotDetectionSetup || isCellSegmentationModel;
  const [isExpanded, setIsExpanded] = useState(shouldExpandByDefault);
  const spotDetectionSetupKey = resultName ?? '';
  const currentSpotDetectionSetup = {
    ...getDefaultSpotDetectionSetup(),
    ...modelPreferencesByModelId?.[spotDetectionSetupKey],
  };
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();
  const spotDetectionProbeNames = showSpotDetectionSetup
    ? uniq(
        compact(
          flatMap(orchestrations, (orchestration) =>
            map(getDetectionLabelOptions(orchestration?.params?.data ?? []), 'id')
          )
        )
      )
    : [];
  const spotDetectionProbeDisplayNames: Record<string, string> = {};
  const perProbeCountBinsSetup: Record<string, ProbeCountBinsSetup> = {};
  forEach(spotDetectionProbeNames, (probeName) => {
    spotDetectionProbeDisplayNames[probeName] = getProbeDisplayName(probeName, stainTypeIdToDisplayName);
    const probePreferences = modelPreferencesByModelId?.[probeName];
    if (!isEmpty(probePreferences?.countBins)) {
      perProbeCountBinsSetup[probeName] = {
        countBins: probePreferences.countBins,
        positivityThresholdScore: probePreferences.positivityThresholdScore,
      };
    }
  });
  const selectedResultCount = showSpotDetectionSetup
    ? compact(map(values(slides), (slide) => selectedOrchestrationsByResultName?.[slide.slideId]?.[resultName ?? '']))
        .length
    : 0;
  const selectedCellSegmentationCount = isCellSegmentationModel
    ? compact(
        map(
          values(slides),
          (slide) => selectedOrchestrations?.[slide.slideId]?.[modelTypeCellSegmentation.apiModelValue]
        )
      ).length
    : 0;
  const collapsedSelectionSummary = showSpotDetectionSetup
    ? `${selectedResultCount} selected · ${getSpotDetectionSetupSummary(
        currentSpotDetectionSetup,
        availableCellSegmentationGeometries
      )}`
    : isCellSegmentationModel
    ? `${selectedCellSegmentationCount} selected`
    : undefined;
  const isAreaModel = modelType === modelTypeArea.apiModelValue;
  const isBinaryClassifierModel = modelType === modelTypeBinaryClassifier.apiModelValue;
  const showBinaryClassifierMultipleSelectionWarning =
    isBinaryClassifierModel && binaryClassifierSelectionMode === 'multiple';
  const selectedAreaModelIds = isAreaModel
    ? uniq(
        compact(
          map(values(slides), (slide) => {
            if (slide.stainingType !== stain) {
              return undefined;
            }

            const selectedAreaResult = selectedOrchestrations?.[slide.slideId]?.[modelTypeArea.apiModelValue];
            return selectedAreaResult?.model?.modelId ?? getModelId(selectedAreaResult?.model?.modelUrl);
          })
        )
      )
    : [];
  const shouldTransferAreasByDefault = uniq(map(values(slides), 'stainingType')).length > 1;
  const transferAreasToRegisteredStains = every(
    selectedAreaModelIds,
    (modelId) => modelPreferencesByModelId?.[modelId]?.transferAreasToRegisteredStains ?? shouldTransferAreasByDefault
  );
  const resultContent = (
    <>
      <TreeView
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
        sx={{
          maxWidth: '100%',
          minWidth: 0,
          '& .MuiTreeItem-content, & .MuiTreeItem-label': { maxWidth: '100%', minWidth: 0 },
        }}
      >
        {!isEmpty(modelInferences)
          ? map(keys(modelInferences), (modelInferenceUrl) => (
              <ModelRow
                key={modelInferenceUrl}
                stain={stain}
                modelType={modelType}
                modelInferenceUrl={modelInferenceUrl}
                stainModelInferences={stainModelInferences}
                slides={slides}
                selectedOrchestrations={selectedOrchestrations}
                setSelectedOrchestrations={setSelectedOrchestrations}
                studyId={studyId}
                modelPreferencesByModelId={modelPreferencesByModelId}
                setModelPreferencesByModelId={setModelPreferencesByModelId}
                setSelectedOrchestrationsByClassificationType={setSelectedOrchestrationsByClassificationType}
                setIntensityClassificationBinning={setIntensityClassificationBinning}
                binaryClassifierSelectionMode={binaryClassifierSelectionMode}
                disableIfNoJob={disableIfNoJob}
              />
            ))
          : !isEmpty(orchestrations)
          ? map(orderBy(orchestrations, 'createdAt', 'desc'), (orchestration) => (
              <OrchestrationRow
                key={orchestration.orchestrationId}
                resultName={resultName}
                orchestration={orchestration}
                selectedOrchestrations={selectedOrchestrationsByResultName}
                onCheck={(newOrchestration: OrchestrationInference, slideIds: string[], checked: boolean) => {
                  setSelectedOrchestrationsByResultName(slideIds, resultName, checked ? newOrchestration : null);
                }}
                slides={slides}
                studyId={studyId}
                disableIfNoJob={disableIfNoJob}
              />
            ))
          : null}
      </TreeView>
      {showSpotDetectionSetup && (
        <SpotDetectionInputSetup
          setup={currentSpotDetectionSetup}
          availableGeometries={availableCellSegmentationGeometries}
          onChange={(setup) => {
            setModelPreferencesByModelId(spotDetectionSetupKey, {
              ...modelPreferencesByModelId?.[spotDetectionSetupKey],
              ...setup,
            });
          }}
          probeNames={spotDetectionProbeNames}
          probeDisplayNames={spotDetectionProbeDisplayNames}
          perProbeSetup={perProbeCountBinsSetup}
          onProbeSetupChange={(probeName, probeSetup) =>
            setModelPreferencesByModelId(probeName, {
              ...modelPreferencesByModelId?.[probeName],
              countBins: probeSetup?.countBins,
              positivityThresholdScore: probeSetup?.positivityThresholdScore,
            })
          }
        />
      )}
    </>
  );

  if (showSpotDetectionSetup && resultName === SPOT_DETECTION_RESULT_NAME) {
    return resultContent;
  }

  return (
    <Accordion disableGutters expanded={isExpanded} onChange={(_event, expanded) => setIsExpanded(expanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Grid
          container
          direction="row"
          wrap="nowrap"
          alignItems="center"
          justifyContent="space-between"
          sx={{ minWidth: 0 }}
        >
          <Grid item sx={{ minWidth: 0 }}>
            <Grid container direction="column" spacing={0.5}>
              <Grid item>
                {isAreaModel
                  ? 'Area (Segmentation)'
                  : modelType
                  ? modelTypesByApiModelValue[modelType]?.text ?? modelType
                  : startCase(resultName)}
              </Grid>
              {!isExpanded && collapsedSelectionSummary && (
                <Grid item>
                  <Typography variant="caption" color="text.secondary">
                    {collapsedSelectionSummary}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item sx={{ flexShrink: 0 }}>
            {isAreaModel && !isEmpty(selectedAreaModelIds) && setModelPreferencesByModelId && (
              <Grid item onClick={(event) => event.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={transferAreasToRegisteredStains}
                      onChange={(_event, checked) => {
                        forEach(selectedAreaModelIds, (modelId) =>
                          setModelPreferencesByModelId(modelId, {
                            ...modelPreferencesByModelId?.[modelId],
                            transferAreasToRegisteredStains: checked,
                          })
                        );
                      }}
                    />
                  }
                  label="Transfer areas to registered stains"
                />
              </Grid>
            )}
            {isBinaryClassifierModel && setBinaryClassifierSelectionMode && (
              <Grid
                container
                direction="column"
                spacing={1}
                alignItems="flex-end"
                onClick={(event) => event.stopPropagation()}
              >
                <Grid item>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={binaryClassifierSelectionMode ?? 'single'}
                    onChange={(_event, nextMode: BinaryClassifierSelectionMode | null) => {
                      if (nextMode) {
                        setBinaryClassifierSelectionMode(nextMode);
                      }
                    }}
                    aria-label="Binary classifier result selection mode"
                  >
                    <ToggleButton value="single" aria-label="Single binary classifier result per slide">
                      Single result per slide
                    </ToggleButton>
                    <ToggleButton value="multiple" aria-label="Multiple binary classifier results per slide">
                      Multiple results per slide
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                {showBinaryClassifierMultipleSelectionWarning && (
                  <Grid item>
                    <Alert severity="warning" sx={{ maxWidth: 560 }}>
                      {binaryClassifierMultipleSelectionWarning}
                    </Alert>
                  </Grid>
                )}
              </Grid>
            )}
            {showBinning && (
              <Grid item>
                <Tooltip title="Bins">
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      setAnchorElIntensityBins(event.currentTarget);
                    }}
                  >
                    <LinearScaleIcon />
                  </IconButton>
                </Tooltip>
                <Popover
                  onClick={(e) => e.stopPropagation()}
                  key={resultName}
                  open={Boolean(anchorElIntensityBins)}
                  anchorEl={anchorElIntensityBins}
                  onClose={handleCloseIntensityBins}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{ paper: { style: { width: '500px' } } }}
                >
                  <IntensityBins
                    binning={{
                      numBins: modelPreferencesByModelId?.[resultName]?.numBins,
                      mapValuesToBins: modelPreferencesByModelId?.[resultName]?.mapValuesToBins,
                    }}
                    setBinning={(binning: ClassificationBinningParams) => {
                      setIntensityClassificationBinning(resultName, binning);
                    }}
                    onClosed={handleCloseIntensityBins}
                  />
                </Popover>
              </Grid>
            )}
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>{resultContent}</AccordionDetails>
    </Accordion>
  );
};

export default InferenceModel;
